import React from "react";
import { ArrowLeft, Clock, Lock, QrCode, UtensilsCrossed } from "lucide-react";

// Primeira tela de quem escaneou o QR, e também a tela de quando não dá pra
// pedir. Quem está com o celular na mão sentado no salão merece uma página da
// LOJA explicando o motivo — logo, nome e cor do estabelecimento — em vez de
// um erro cru. O aviso não tem botão de saída: o "VER O CARDÁPIO" que existia
// aqui abria o cardápio online, e o cliente pedia retirada achando que era a
// mesa (dono, 03/10/2026). Em troca a tela se libera sozinha quando o motivo
// acaba (useBloqueioSeLibera); `libera` marca os motivos que acabam sem o
// cliente fazer nada, para avisar que não precisa ler o QR de novo.
const BLOQUEIOS = {
  QR_DESLIGADO: {
    icone: QrCode,
    titulo: "Pedido pelo celular indisponível",
    ajuda: "Chame um atendente para fazer seu pedido.",
  },
  QR_INVALIDO: {
    icone: QrCode,
    titulo: "QR Code desatualizado",
    ajuda: "Peça ao atendente o código novo da sua mesa.",
  },
  LOJA_FECHADA: {
    icone: Clock,
    titulo: "Estamos fechados",
    ajuda: "Chame um atendente para fazer seu pedido.",
    libera: true,
  },
  CAIXA_FECHADO: {
    icone: Clock,
    titulo: "Ainda não estamos atendendo",
    ajuda: "Chame um atendente para fazer seu pedido.",
    libera: true,
  },
  MESA_BLOQUEADA: {
    icone: Lock,
    titulo: "Pedidos desta mesa bloqueados",
    ajuda: "Chame um atendente para continuar.",
    libera: true,
  },
  MESA_EM_FECHAMENTO: {
    icone: Lock,
    titulo: "A conta desta mesa já foi fechada",
    ajuda: "Chame um atendente se quiser pedir mais alguma coisa.",
  },
};

function Marca({ logo, loja }) {
  return (
    <div className="flex flex-col items-center gap-3 mb-8">
      {logo ? (
        <img
          src={logo}
          alt={loja || "Logo da loja"}
          className="w-20 h-20 rounded-2xl object-cover bg-white shadow-lg"
        />
      ) : (
        <div className="w-20 h-20 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
          <UtensilsCrossed className="w-10 h-10" />
        </div>
      )}
      {loja ? <p className="text-lg font-black tracking-tight">{loja}</p> : null}
    </div>
  );
}

// Quem leu o QR desta mesa já está em OUTRA (02/10/2026): o caminho de volta
// fica sempre à mão, mesmo quando esta está ocupada ou bloqueada.
function VoltarParaMinhaMesa({ minhaMesa, onVoltar, darkMode }) {
  if (!minhaMesa?.mesa?.token) return null; // só o aparelho que guarda a sessão volta direto
  return (
    <button
      type="button"
      onClick={onVoltar}
      data-e2e="mesa-voltar-minha"
      className={`mt-4 w-full max-w-sm h-12 rounded-full border-2 text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition ${darkMode ? "border-zinc-700 text-zinc-200" : "border-zinc-300 text-zinc-700"}`}
    >
      <ArrowLeft className="w-4 h-4" /> VOLTAR PARA A {String(minhaMesa.mesa.nome || "minha mesa").toUpperCase()}
    </button>
  );
}

export default function TelaEntrada({
  mesa, loja, logo, impedimento, anfitriao, convidado, onEntrar, carregando, darkMode,
  minhaMesa, onVoltarParaMinhaMesa,
}) {
  const fundo = darkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900";

  if (impedimento) {
    const bloqueio = BLOQUEIOS[impedimento.codigo] || {
      icone: QrCode,
      titulo: "Não dá para pedir agora",
      ajuda: "Chame um atendente para fazer seu pedido.",
    };
    const Icone = bloqueio.icone;
    return (
      <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center px-7 text-center ${fundo}`}
           data-e2e="mesa-bloqueada" data-codigo={impedimento.codigo}>
        <Marca logo={logo} loja={loja} />
        <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <Icone className="w-7 h-7" />
        </div>
        <h1 className="text-2xl font-black leading-tight mb-2">{bloqueio.titulo}</h1>
        <p className="text-sm opacity-70 max-w-xs">{impedimento.mensagem}</p>
        {mesa ? (
          <p className="mt-5 text-xs uppercase tracking-widest opacity-50">
            {mesa.nome || `Mesa ${mesa.numero}`}
          </p>
        ) : null}
        <VoltarParaMinhaMesa minhaMesa={minhaMesa} onVoltar={onVoltarParaMinhaMesa} darkMode={darkMode} />
        <p className="mt-6 text-xs opacity-50 max-w-xs">{bloqueio.ajuda}</p>
        {bloqueio.libera ? (
          <p className="mt-1 text-xs opacity-50 max-w-xs" data-e2e="mesa-libera-sozinha">
            Não precisa ler o QR Code de novo: esta tela libera sozinha.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center px-7 text-center ${fundo}`}>
      <Marca logo={logo} loja={loja} />
      <p className="text-sm uppercase tracking-widest opacity-60">Você está na</p>
      <h1 className="text-5xl font-black mt-1 mb-6" data-e2e="mesa-titulo">
        {(mesa?.nome || `Mesa ${mesa?.numero ?? ""}`).toUpperCase()}
      </h1>
      {/* Quem chega primeiro abre a mesa — ao confirmar o CPF, não neste
          botão (24/09/2026); quem chega depois entra nela e espera o
          primeiro liberar pelo celular dele. */}
      <p className="text-base opacity-70 max-w-xs mb-8" data-e2e="mesa-entrada-texto">
        {convidado
          ? `${anfitriao || "A mesa"} te convidou. Informe seu CPF e você entra direto.`
          : anfitriao
            ? `${anfitriao} já abriu esta mesa e vai liberar a sua entrada pelo celular.`
            : "Peça pelo celular sem esperar. Seu pedido vai direto para a cozinha."}
      </p>
      <button
        type="button"
        onClick={onEntrar}
        disabled={carregando}
        data-e2e="mesa-entrar"
        className="w-full max-w-sm h-16 rounded-full bg-primary text-primary-foreground text-lg font-black shadow-lg active:scale-[0.98] transition disabled:opacity-60"
      >
        {carregando ? "Entrando..." : anfitriao || convidado ? "ENTRAR NA MESA" : "ABRIR MESA"}
      </button>
      <VoltarParaMinhaMesa minhaMesa={minhaMesa} onVoltar={onVoltarParaMinhaMesa} darkMode={darkMode} />
    </div>
  );
}
